import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, Loader2, UploadCloud, XCircle } from "lucide-react";
import api from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { SignDateDialog, SIGNED_KEY } from "@/components/SignDateDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DROP_STATUS = {
  reading: { color: "#9a7b33", label: "A ler…" },
  placed: { color: "#16a34a", label: "Colocado" },
  por_confirmar: { color: "#b45309", label: "Por confirmar" },
  nao_reconhecido: { color: "#b91c1c", label: "Não reconhecido" },
  cpcv_convert: { color: "#1d4ed8", label: "CPCV · rever dados" },
};

function DropItem({ it, i, onConfirm }) {
  const st = DROP_STATUS[it.status] || DROP_STATUS.reading;
  return (
    <div className="flex items-start gap-3 text-sm py-2 border-b border-slate-50 last:border-0" data-testid={`drop-item-${i}`}>
      <span className="mt-0.5 shrink-0">
        {it.status === "reading" ? <Loader2 size={16} className="animate-spin text-ec-gold" />
          : it.status === "placed" ? <CheckCircle2 size={16} className="text-green-600" />
          : it.status === "nao_reconhecido" ? <XCircle size={16} className="text-red-600" />
          : <HelpCircle size={16} className="text-amber-600" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ec-green truncate">{it.name}</span>
          <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={{ color: st.color, backgroundColor: st.color + "1a" }}>{st.label}</span>
        </div>
        {it.line && <div className="text-xs text-slate-500 mt-0.5">{it.line}</div>}
        {it.status === "por_confirmar" && (
          <div className="flex items-center gap-2 mt-2">
            <Select onValueChange={(v) => onConfirm(i, v)}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid={`confirm-select-${i}`}><SelectValue placeholder="Escolher documento…" /></SelectTrigger>
              <SelectContent>
                {it.suggestions.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

export function DropZone({ pid, onDone, onCpcv }) {
  const inputRef = useRef();
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [signAi, setSignAi] = useState(null);

  const upd = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const base = items.length;
    setItems((prev) => [...prev, ...files.map((f) => ({ name: f.name, status: "reading", line: null, suggestions: [], file_id: null }))]);
    await Promise.all(files.map(async (f, k) => {
      const idx = base + k;
      try {
        const { data } = await api.post(`/processes/${pid}/documents/auto-upload`, await makeForm([["file", f]]));
        upd(idx, { status: data.status, line: data.line, suggestions: data.suggestions || [], file_id: data.file_id });
        if (data.status === "placed" && data.key === SIGNED_KEY) setSignAi(data.ai || {});
        if (data.status === "cpcv_convert" && onCpcv) onCpcv(data);
      } catch (e) {
        upd(idx, { status: "nao_reconhecido", line: "Falha ao processar." });
      }
    }));
    onDone();
  };

  const confirmInto = async (idx, key) => {
    const it = items[idx];
    await api.post(`/processes/${pid}/documents/${key}/attach-file`, { file_id: it.file_id });
    upd(idx, { status: "placed", line: null });
    onDone();
    if (key === SIGNED_KEY) setSignAi({});
    toast.success("Documento colocado");
  };

  const summary = items.length && !items.some((i) => i.status === "reading")
    ? `${items.filter((i) => i.status === "placed").length} colocado(s), ${items.filter((i) => i.status === "por_confirmar").length} por rever, ${items.filter((i) => i.status === "nao_reconhecido").length} não reconhecido(s)`
    : null;

  return (
    <div className="bg-white rounded-[18px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
      {signAi && <SignDateDialog pid={pid} ai={signAi} onClose={() => setSignAi(null)} onSaved={onDone} />}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="dossier-dropzone"
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-colors p-8 text-center ${dragging ? "border-ec-gold bg-ec-gold/5" : "border-slate-200 hover:border-ec-gold/50"}`}
      >
        <UploadCloud size={30} strokeWidth={1.5} className="mx-auto text-ec-gold" />
        <p className="font-heading text-lg text-ec-green mt-2">Largue aqui todos os documentos</p>
        <p className="text-sm text-slate-500 mt-1">Lemos, classificamos e colocamos cada um no sítio certo.</p>
        <input ref={inputRef} type="file" multiple accept=".pdf,image/*,.docx" className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="dropzone-input" />
      </div>

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          {items.map((it, i) => <DropItem key={i} it={it} i={i} onConfirm={confirmInto} />)}
          {summary && <div className="text-sm font-medium text-ec-green pt-2" data-testid="drop-summary">{summary}</div>}
        </div>
      )}
    </div>
  );
}
